import React, { useCallback, useEffect, useState } from "react";
import { Snackbar, Switch } from "@mui/material";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { Button } from "../shared/Button/Button";
import { TableModal } from "../shared/Modal/TableModal";
import { GeneralInfoWrapper, GeneralInfoText } from "./Profile.styles";
import {
  ActiveMethodText,
  DialogButtonRow,
  EnableMethodDescription,
  EnableMethodInfo,
  EnableMethodTitle,
  InactiveStatusText,
  MfaContent,
  MfaDescription,
  MfaErrorToastAlert,
  MfaLabel,
  MfaModalConfirmSpinnerIcon,
  MfaRow,
  MfaToggleSpinner,
} from "./MfaSettings.styles";
import {
  GetMfaStatus,
  UpdateMfaStatus,
  MfaMethod,
  MfaStatusResponse,
  MfaUpdateResponse,
} from "./Services/MfaService";
import {
  isProviderUniversalLoginConfigured,
  startProviderUniversalLogout,
} from "../../auth/providerUniversalLogin";

type ConfirmationDialog = "enable" | "disable" | null;

type ErrorToastState = { open: boolean; message: string };

const initialErrorToast: ErrorToastState = { open: false, message: "" };
const MFA_ERROR_TOAST_AUTO_HIDE_MS = 6000;

const METHOD_LABELS: Record<MfaMethod, string> = {
  email: "Email",
  otp: "Authenticator App",
};
const TOAST_ANCHOR = { vertical: "bottom", horizontal: "left" } as const;
const CONFIRM_SPINNER = <MfaModalConfirmSpinnerIcon icon={faSpinner} spin />;

const MfaSettings: React.FC = () => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { logout } = useAuth0();
  const { deviceType } = useDeviceHook();
  const [mfaStatus, setMfaStatus] = useState<MfaStatusResponse | null>(null);
  const [isFetchingStatus, setIsFetchingStatus] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorToast, setErrorToast] =
    useState<ErrorToastState>(initialErrorToast);
  const [confirmationDialog, setConfirmationDialog] =
    useState<ConfirmationDialog>(null);

  const fetchMfaStatus = useCallback(async () => {
    if (!auth?.token) {
      setIsFetchingStatus(false);
      return;
    }

    setIsFetchingStatus(true);
    const response = await GetMfaStatus(auth.token, setLoggedOut);

    if (response?.ok) {
      const data: MfaStatusResponse = await response.json();
      setMfaStatus(data);
    } else {
      setMfaStatus(null);
    }

    setIsFetchingStatus(false);
  }, [auth?.token, setLoggedOut]);

  useEffect(() => {
    fetchMfaStatus();
  }, [fetchMfaStatus]);

  const isMfaEnabled = Boolean(mfaStatus?.mfa_enabled);

  const handleToggleClick = () => {
    if (isSubmitting) return;
    setConfirmationDialog(isMfaEnabled ? "disable" : "enable");
  };

  const closeDialog = () => setConfirmationDialog(null);

  const handleModalClose = () => {
    if (isSubmitting) return;
    closeDialog();
  };

  const handleErrorToastClose = () => {
    setErrorToast((prev) => ({ ...prev, open: false }));
  };

  const submitMfaChange = async (enabled: boolean, method?: MfaMethod) => {
    if (!auth?.token) return;
    setErrorToast(initialErrorToast);
    setIsSubmitting(true);

    const response = await UpdateMfaStatus(
      { enabled, ...(method && { method }) },
      auth.token,
      setLoggedOut,
    );

    if (response?.ok) {
      const data: MfaUpdateResponse = await response.json();
      if (data.force_logout) {
        setIsSubmitting(false);
        closeDialog();
        if (isProviderUniversalLoginConfigured()) {
          startProviderUniversalLogout(logout);
        } else {
          setLoggedOut(false);
        }
        return;
      }
      setMfaStatus(data);
    } else {
      const action = enabled ? "enable" : "disable";
      setErrorToast({
        open: true,
        message: `Failed to ${action} two-factor authentication. Please try again.`,
      });
    }
    setIsSubmitting(false);
    closeDialog();
  };

  if (!isFetchingStatus && !mfaStatus) {
    return null;
  }

  return (
    <>
      <GeneralInfoWrapper>
        <GeneralInfoText>SECURITY</GeneralInfoText>
        <MfaRow>
          <MfaContent>
            <MfaLabel>Two-Factor Authentication</MfaLabel>
            <MfaDescription id="mfa-description">
              Add an extra layer of security to your account by requiring a
              verification code at sign in.
            </MfaDescription>
            {mfaStatus && !isMfaEnabled && (
              <InactiveStatusText aria-live="polite">
                Not configured
              </InactiveStatusText>
            )}
            {mfaStatus && isMfaEnabled && mfaStatus.mfa_method && (
              <ActiveMethodText aria-live="polite">
                Active:{" "}
                {METHOD_LABELS[mfaStatus.mfa_method] ?? mfaStatus.mfa_method}
              </ActiveMethodText>
            )}
          </MfaContent>
          {isFetchingStatus ? (
            <MfaToggleSpinner icon={faSpinner} spin />
          ) : (
            <Switch
              color="primary"
              checked={isMfaEnabled}
              onChange={handleToggleClick}
              disabled={isSubmitting}
              inputProps={{
                "aria-label": "Toggle two-factor authentication",
                "aria-describedby": "mfa-description",
                ...(isSubmitting ? { "aria-busy": true } : {}),
              }}
            />
          )}
        </MfaRow>

        <TableModal
          open={confirmationDialog === "enable"}
          onClose={handleModalClose}
          title="ENABLE TWO-FACTOR AUTHENTICATION"
          deviceType={deviceType}
          message="You will be signed out to complete setup on your next sign in."
          footer={
            <DialogButtonRow>
              <Button
                label="CANCEL"
                size="small"
                reverse
                onClick={handleModalClose}
                disabled={isSubmitting}
              />
              <Button
                label={isSubmitting ? "" : "ENABLE"}
                size="small"
                onClick={() => submitMfaChange(true, "email")}
                disabled={isSubmitting}
                testId="mfa-enable-confirm"
                startIcon={isSubmitting ? CONFIRM_SPINNER : undefined}
              />
            </DialogButtonRow>
          }
        >
          <EnableMethodInfo data-testid="mfa-method-email">
            <EnableMethodTitle>Email</EnableMethodTitle>
            <EnableMethodDescription>
              Receive a 6-digit verification code to your email each time you
              sign in.
            </EnableMethodDescription>
          </EnableMethodInfo>
        </TableModal>

        <TableModal
          open={confirmationDialog === "disable"}
          onClose={handleModalClose}
          title="DISABLE TWO-FACTOR AUTHENTICATION"
          deviceType={deviceType}
          message="This will reduce the security of your account. Are you sure you want to disable two-factor authentication?"
          footer={
            <DialogButtonRow>
              <Button
                label="CANCEL"
                size="small"
                reverse
                onClick={handleModalClose}
                disabled={isSubmitting}
              />
              <Button
                label={isSubmitting ? "" : "DISABLE"}
                size="small"
                backgroundColor="redMedium"
                onClick={() => submitMfaChange(false)}
                disabled={isSubmitting}
                testId="mfa-disable-confirm"
                startIcon={isSubmitting ? CONFIRM_SPINNER : undefined}
              />
            </DialogButtonRow>
          }
        >
          <></>
        </TableModal>
      </GeneralInfoWrapper>

      <Snackbar
        open={errorToast.open}
        autoHideDuration={MFA_ERROR_TOAST_AUTO_HIDE_MS}
        onClose={handleErrorToastClose}
        anchorOrigin={TOAST_ANCHOR}
      >
        <MfaErrorToastAlert
          onClose={handleErrorToastClose}
          severity="error"
          variant="filled"
        >
          {errorToast.message}
        </MfaErrorToastAlert>
      </Snackbar>
    </>
  );
};

export default MfaSettings;
